import { Children, Fragment, ReactNode, cloneElement } from 'react';
import { useHistory } from 'react-router-dom';
import useCalendly from '../../openCalendly';
import GenericCTAButton from '../CTAButton/CTAButton';
import cx from 'classnames';
import styles from './CTASegment.module.scss';
import React from 'react';
import Documents from '../../Documents';

interface HeadingProps {
  children: ReactNode;
  className?: string;
}

interface CTASegmentProps {
  className?: string;
  children?: ReactNode;
  defaultCTAButtons?: boolean;
  greyBackground?: boolean;
}

interface ChildrenProp {
  children?: ReactNode;
  secondary?: boolean;
  bookingUrl?: string;
}

export const Heading = ({ children, className }: HeadingProps): JSX.Element => {
  return (
    <h3
      className={cx(styles.heading, {
        [`${styles.noheading}`]: children === '',
        [`${className}`]: className,
      })}
    >
      {children}
    </h3>
  );
};

export const Subheading = ({ children, className }: HeadingProps): JSX.Element => {
  return (
    <p
      className={cx(styles.subheading, {
        [`${styles.noSubheading}`]: children === '',
        [`${className}`]: className,
      })}
    >
      {children}
    </p>
  );
};

export function computeBookingUrl(): string {
  const asOffsetInMinutes = getOffsetInMinutes('America/Toronto');
  const npOffsetInMinutes = getOffsetInMinutes('Asia/Dubai');

  // we take the negative because getTimezoneOffset returns the offset in minutes from local time to UTC
  const visitorOffsetInMinutes = -new Date().getTimezoneOffset();
  const asDiff = Math.abs(visitorOffsetInMinutes - asOffsetInMinutes);
  const npDiff = Math.abs(visitorOffsetInMinutes - npOffsetInMinutes);
  if (asDiff < npDiff) {
    return Documents.bookDemo;
  }

  return Documents.bookDemoNeelum;

  // compute the offset in minutes from UTC for a given timezone, usinthe current time and date
  function getOffsetInMinutes(timeZone: string): number {
    const str = new Date().toLocaleString('en', { timeZone, timeZoneName: 'longOffset' });
    const [_dontcare, h, m] = str.match(/([+-]\d+):(\d+)$/) || ['dontcare', '+00', '00'];
    const hours = parseInt(h);
    const minutes = parseInt(m);
    return hours * 60 + (hours > 0 ? +minutes : -minutes);
  }
}

export const OpenCalendlyAnchor = ({ children, ...props }: ChildrenProp) => {
  const bookingUrl = props.bookingUrl || Documents.bookDemo;
  return (
    <a href={bookingUrl} target='_blank' rel='noreferrer noopener'>
      {children ? children : 'book a demo'}
    </a>
  );
};

export const OpenCalendlyCTA = ({ children, ...props }: ChildrenProp) => {
  const bookingUrl = props.bookingUrl || computeBookingUrl();
  const openCalendly = useCalendly(bookingUrl);
  return (
    <GenericCTAButton onClick={() => openCalendly()} {...props}>
      {children ? children : 'book a demo'}
    </GenericCTAButton>
  );
};

export const RequestInfoCTA = ({ ...props }) => {
  const history = useHistory();
  return (
    <GenericCTAButton onClick={() => history.push('/requestInfo')} {...props}>
      request info
    </GenericCTAButton>
  );
};

export const RequestInfoCTAAlt = ({ ...props }) => {
  const history = useHistory();
  return (
    <GenericCTAButton onClick={() => history.push('/requestInfo')} {...props}>
      request a quote
    </GenericCTAButton>
  );
};

const CTASegment = ({ ...props }: CTASegmentProps): JSX.Element => {
  const { className, children, defaultCTAButtons = true, greyBackground } = props;

  const headingChild = Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type === Heading) {
      return cloneElement(<>{child}</>);
    }
  });

  const subheadingChild = Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type === Subheading) {
      return cloneElement(<>{child}</>);
    }
  });

  const ctaButtonsChild = Children.map(children, (child) => {
    if (
      React.isValidElement(child) &&
      (child.type === GenericCTAButton || child.type === OpenCalendlyCTA || child.type === RequestInfoCTA)
    ) {
      return cloneElement(<>{child}</>);
    }
  });

  return (
    <section
      className={cx(
        styles.ctaSegmentContainer,
        {
          [styles.greyBackground]: greyBackground,
        },
        className,
      )}
    >
      {headingChild?.length ? (
        <Fragment>{headingChild}</Fragment>
      ) : (
        <h3 className={styles.heading}>Ready to bring Peekapak to your school or district?</h3>
      )}
      {subheadingChild?.length ? (
        <Fragment>{subheadingChild}</Fragment>
      ) : (
        <p className={styles.subheading}>
          Join thousands of educators that trust Peekapak to support student learning.
        </p>
      )}
      <nav
        className={cx(styles.buttonsContainer, {
          [`${styles.noButtons}`]: !defaultCTAButtons,
        })}
      >
        {ctaButtonsChild?.length ? ctaButtonsChild : null}
        {!ctaButtonsChild?.length && defaultCTAButtons && (
          <React.Fragment>
            <OpenCalendlyCTA />
            <RequestInfoCTA secondary />
          </React.Fragment>
        )}
      </nav>
    </section>
  );
};

export default CTASegment;
